import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CListGroup } from '@coreui/react';
import { useSelector } from 'react-redux';
import { maxBy, chain } from 'lodash';
import { selectChats } from '../../selectors/chat.selector';
import Contact from './Contact';
import { CONTACT_SEARCH_FIELDS } from '../../../shared/constants/chat.constants';

interface ChatListProps {
  activeChat: string;
  onSelectChat: (chat: string) => void;
  searchQuery: string;
}

const RECORDS_PER_PAGE = 10;

const ContactList = (props: ChatListProps) => {
  const {
    activeChat,
    onSelectChat,
    searchQuery
  } = props;
  const chats = useSelector(selectChats);
  const [visibleCount, setVisibleCount] = useState(RECORDS_PER_PAGE);
  const containerRef = useRef<HTMLDivElement>(null);

  const makeSubstringBold = (item, query) => {
    if (!query) return item;
    const regex = new RegExp(query, 'gi');
    return item.replace(regex, (match) => `<span class="font-weight-bolder">${match}</span>`);
  };

  const highlightSearchQueryInChatInfo = (item, query) => {
    let result = {...item};
    let isFound = false;
    CONTACT_SEARCH_FIELDS.forEach((field) => {
      const value = item?.[field]?.toString();
      if (value?.toLowerCase?.()?.indexOf?.(query?.toLowerCase?.()) > -1) {
        result = {
          ...result,
          [field]: makeSubstringBold(value, query)
        };
        isFound = true;
      }
    });
    return isFound ? result : null;
  };

  const filteredChats = useMemo(() => {
    if (chats) {
      let chatResults = chain(chats).map(chat => chat);
      if (searchQuery) {
        chatResults = chatResults.reduce((result, item) => {
          const chatInfo = highlightSearchQueryInChatInfo(item?.chatInfo, searchQuery);
          if (chatInfo) {
            result.push({
              ...item,
              chatInfo
            });
          }
          return result;
        }, []);
      }

      return chatResults
        .orderBy((a) => {
          return maxBy(chats?.[a?.chat_id]?.messages, 'created_time')?.created_time ?? 0;
        }, 'desc')
        .filter((item) => {
          // A chat can end up in state with no chatInfo if its channel was
          // visited directly (e.g. via URL) without ever being subscribed
          // through the task list - nothing to show for it, so skip it here
          // rather than rendering a row of "undefined" fields.
          return !!item?.chatInfo;
        })
        .value();
    }
    return [];
  }, [chats, searchQuery]);

  useEffect(() => {
    setVisibleCount(RECORDS_PER_PAGE);
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [searchQuery]);

  const visibleItems = useMemo(() => filteredChats.slice(0, visibleCount), [filteredChats, visibleCount]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 100 && visibleCount < filteredChats.length) {
      setVisibleCount((count) => Math.min(count + RECORDS_PER_PAGE, filteredChats.length));
    }
  };

  return (
    <div className={`chat-list ${activeChat ? 'hide' : ''}`}>
      <div
        ref={containerRef}
        className={`chat-list-container ${activeChat ? 'hide' : ''}`}
        onScroll={handleScroll}
      >
        {
          visibleItems?.length > 0 ? (
            <CListGroup>
              {
                visibleItems?.map((chat) => {
                  return  <Contact key={chat?.chat_id} chat={chat} activeChatId={activeChat} onSelectChat={onSelectChat}/>
                })
              }
            </CListGroup>
          ) : (
            <div className="d-flex justify-content-center align-items-stretch flex-grow-1 font-weight-bold mt-3">
              No Result Found
            </div>
          )
        }
      </div>
    </div>
  );
};

export default ContactList;
